import { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Skeleton, Empty, Tag, Tooltip } from "antd";
import {
  CloseOutlined,
  WarningOutlined,
  CopyOutlined,
  CheckOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  PlusCircleOutlined,
  TagOutlined,
  MinusCircleOutlined,
  StopOutlined,
  CreditCardOutlined,
  RollbackOutlined,
  BranchesOutlined,
  EditOutlined,
  UserSwitchOutlined,
  WalletOutlined,
  ProfileOutlined,
  UserOutlined,
  ClockCircleOutlined,
  CodeOutlined,
  DownOutlined,
  RightOutlined,
  LeftOutlined,
  CalendarOutlined,
  TeamOutlined,
  LogoutOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { useViewport } from "../../../hooks/useViewport";
import {
  useAuditLogDetail,
  useBillTimeline,
  useBillTimelineDetails,
} from "../../../hooks/useAuditLogs";
import { modalStyles } from "../../../utils/modalStyles";
import type { AuditLogEntry, AuditLogDetail } from "../../../api/auditLogs";
import { useAuditLookups } from "./humanize/useAuditLookups";
import {
  interpretAudit,
  statusInfo,
  type HumanStory,
  type Tone,
} from "./humanize/interpret";
import { buildTimeline } from "./humanize/timeline";
import { eur } from "./humanize/format";
import { PATHS, REQ, NEW_BILL_ID } from "./humanize/contract";
import { isSeatOpeningNewBill } from "./humanize/interpretReservations";

const R = {
  bg: "var(--color-canvas)",
  surface: "var(--color-surface)",
  border: "var(--color-shade-2)",
  borderMed: "var(--color-shade-3)",
  text: "var(--color-ink)",
  textSoft: "var(--color-body)",
  textMuted: "var(--color-muted)",
  ink: "var(--color-ink)",
  brand: "var(--color-brand-ink)",
  brandDeep: "var(--color-brand-deep-ink)",
  brandTint: "var(--color-brand-tint)",
  dangerDeep: "var(--color-danger-ink)",
  dangerTint: "var(--color-danger-tint)",
  successInk: "var(--color-ok-deep)",
  successTint: "var(--color-ok-tint)",
  mono: '"JetBrains Mono", ui-monospace, monospace',
};

const TONES: Record<Tone, { tint: string; ink: string }> = {
  add: { tint: "var(--color-ok-tint)", ink: "var(--color-ok-deep)" },
  discount: { tint: R.brandTint, ink: R.brandDeep },
  remove: { tint: "var(--color-danger-tint)", ink: "var(--color-danger-ink)" },
  money: { tint: "var(--color-fill)", ink: "var(--color-brand-deep-ink)" },
  info: { tint: "var(--color-fill)", ink: "var(--color-body)" },
  neutral: { tint: "var(--color-fill)", ink: "var(--color-body)" },
};

const KIND_ICONS: Record<string, React.ReactNode> = {
  new: <PlusCircleOutlined />,
  add: <PlusCircleOutlined />,
  applyDiscount: <TagOutlined />,
  applyItemDiscount: <TagOutlined />,
  removeDiscount: <MinusCircleOutlined />,
  cancelItem: <StopOutlined />,
  cancelItems: <StopOutlined />,
  cancelAll: <StopOutlined />,
  close: <CreditCardOutlined />,
  restore: <RollbackOutlined />,
  split: <BranchesOutlined />,
  splitAndPay: <BranchesOutlined />,
  rename: <EditOutlined />,
  changeWaiter: <UserSwitchOutlined />,
  cashIn: <WalletOutlined />,
  cashOut: <WalletOutlined />,
  resCreate: <CalendarOutlined />,
  resEdit: <EditOutlined />,
  resSeat: <TeamOutlined />,
  resRelease: <LogoutOutlined />,
  resUnseat: <RollbackOutlined />,
  resCancel: <StopOutlined />,
  resRestore: <RollbackOutlined />,
  generic: <ProfileOutlined />,
};

const METHOD_COLORS: Record<string, { bg: string; fg: string }> = {
  GET: { bg: "var(--color-fill)", fg: "var(--color-body)" },
  POST: { bg: "var(--color-ok-tint)", fg: "var(--color-ok-deep)" },
  PATCH: { bg: R.brandTint, fg: R.brandDeep },
  PUT: { bg: "var(--color-violet-tint)", fg: "var(--color-violet)" },
  DELETE: { bg: "var(--color-danger-tint)", fg: "var(--color-danger-ink)" },
};

function formatWhen(
  iso: string,
  lang: string,
  t: (k: string) => string,
): string {
  const d = new Date(iso);
  const now = new Date();
  const time = d.toLocaleTimeString(lang, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  if (d.toDateString() === now.toDateString())
    return `${t("common.today")}, ${time}`;
  const yest = new Date(now);
  yest.setDate(now.getDate() - 1);
  if (d.toDateString() === yest.toDateString())
    return `${t("common.yesterday")}, ${time}`;
  return `${d.toLocaleDateString(lang)} ${time}`;
}

// Дали операцията отваря нова сметка (POST /bills с billId === 0 или
// „Настани" на резервация, отворило нова празна сметка). Маркира началото на
// нов "живот" на сметката (id-тата се преизползват между сесии).
function isNewBillOp(
  item: AuditLogEntry,
  detail: AuditLogDetail | undefined,
): boolean {
  if (isSeatOpeningNewBill(detail)) return true;
  if (item.method !== "POST" || item.path !== PATHS.billsCollection)
    return false;
  const body = detail?.request_body;
  return (
    !!body &&
    typeof body === "object" &&
    (body as Record<string, unknown>)[REQ.billId] === NEW_BILL_ID
  );
}

// Връща само операциите от текущия живот на сметката (този, който съдържа
// активния запис) — реже остатъци от предишни сесии със същия bill_id.
function sessionSlice(
  items: AuditLogEntry[],
  detailById: Map<number, AuditLogDetail | undefined>,
  activeId: number | null,
): AuditLogEntry[] {
  if (items.length === 0) return items;
  const ci = items.findIndex((i) => i.id === activeId);
  if (ci < 0) return items;
  let start = 0;
  let end = items.length - 1;
  for (let i = 0; i <= ci; i++) {
    if (isNewBillOp(items[i], detailById.get(items[i].id))) start = i;
  }
  for (let i = ci + 1; i < items.length; i++) {
    if (isNewBillOp(items[i], detailById.get(items[i].id))) {
      end = i - 1;
      break;
    }
  }
  return items.slice(start, end + 1);
}

interface Props {
  entry: AuditLogEntry | null;
  open: boolean;
  onClose: () => void;
}

export default function AuditLogDetailModal({ entry, open, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const { isMobile, isNarrow, isTablet } = useViewport();
  const [showRaw, setShowRaw] = useState(false);
  const [activeEntry, setActiveEntry] = useState<AuditLogEntry | null>(entry);
  // Складовите справочници (продукти, складове, доставчици, рецепти) се дърпат
  // само когато отвореният запис е складов — иначе одит страницата би пуснала
  // четири заявки, чиито имена никой запис няма да поиска.
  const lookups = useAuditLookups(
    open && (activeEntry?.path?.startsWith("/inventory") ?? false),
  );
  const [navTick, setNavTick] = useState(0);

  useEffect(() => {
    setActiveEntry(entry);
    setShowRaw(false);
    setNavTick(0);
  }, [entry?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const navigateTo = (e: AuditLogEntry) => {
    setActiveEntry(e);
    setNavTick((n) => n + 1);
  };

  const { data, isLoading, error } = useAuditLogDetail(
    open ? (activeEntry?.id ?? null) : null,
  );

  // Всички записи по тази сметка (мутации).
  const billId = data?.bill_id ?? activeEntry?.bill_id ?? null;
  const { data: timelineData } = useBillTimeline(open ? billId : null);
  const allItems = useMemo(
    () => (timelineData?.items ?? []).filter((i) => i.method !== "GET"),
    [timelineData],
  );

  // Детайлите на всички записи (за салдо, "нова сметка" маркер и разгъване).
  const ids = allItems.map((i) => i.id);
  const detailResults = useBillTimelineDetails(ids, open && ids.length > 0);
  const detailById = new Map<number, AuditLogDetail | undefined>();
  ids.forEach((id, i) => detailById.set(id, detailResults[i]?.data));

  // Само текущият живот на сметката.
  const sessionItems = sessionSlice(
    allItems,
    detailById,
    activeEntry?.id ?? null,
  );
  const activeIndex = sessionItems.findIndex((i) => i.id === activeEntry?.id);
  const hasJourney = sessionItems.length > 1 && activeIndex >= 0;
  const goRel = (delta: number) => {
    const ni = activeIndex + delta;
    if (ni >= 0 && ni < sessionItems.length) navigateTo(sessionItems[ni]);
  };

  const modalWidth = isMobile
    ? undefined
    : isNarrow
      ? 460
      : isTablet
        ? 560
        : 680;
  const method = activeEntry?.method ?? "";
  const methodTone = METHOD_COLORS[method] || { bg: "var(--color-fill)", fg: "var(--color-body)" };

  const story = useMemo<HumanStory | null>(
    () => (data ? interpretAudit(data, lookups, t) : null),
    [data, lookups, t],
  );
  const status = data ? statusInfo(data) : null;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={modalWidth}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
      title={null}
    >
      <style>{`
        .al-code-block::-webkit-scrollbar,
        .al-scroll::-webkit-scrollbar { height: 8px; width: 8px; }
        .al-code-block::-webkit-scrollbar-track,
        .al-scroll::-webkit-scrollbar-track { background: var(--color-shade-1); }
        .al-code-block::-webkit-scrollbar-thumb,
        .al-scroll::-webkit-scrollbar-thumb {
          background: var(--color-shade-4);
          border-radius: 4px;
        }
        .al-code-block::-webkit-scrollbar-thumb:hover,
        .al-scroll::-webkit-scrollbar-thumb:hover {
          background: var(--color-shade-5);
        }
      `}</style>
      <div
        style={{
          background: R.surface,
          display: "flex",
          flexDirection: "column",
          maxHeight: "90vh",
        }}
      >
        {/* Header — method + path + status + journey nav + close */}
        <div
          style={{
            padding: isNarrow ? "10px 12px" : "12px 16px",
            borderBottom: `1px solid ${R.border}`,
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexShrink: 0,
          }}
        >
          <Tag
            style={{
              background: methodTone.bg,
              color: methodTone.fg,
              border: "none",
              fontFamily: R.mono,
              fontSize: 11,
              fontWeight: 700,
              borderRadius: 6,
              padding: "2px 8px",
              margin: 0,
              flexShrink: 0,
            }}
          >
            {method}
          </Tag>
          <span
            style={{
              flex: 1,
              minWidth: 0,
              fontFamily: R.mono,
              fontSize: 12,
              color: R.textMuted,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={activeEntry?.path}
          >
            {activeEntry?.path}
          </span>

          {status && <StatusPill ok={status.ok} />}

          {hasJourney && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexShrink: 0,
              }}
            >
              <NavBtn
                disabled={activeIndex <= 0}
                onClick={() => goRel(-1)}
                label={t("auditLogDetail.human.prevOp")}
                icon={<LeftOutlined style={{ fontSize: 12 }} />}
              />
              <span
                style={{
                  fontFamily: R.mono,
                  fontSize: 11,
                  color: R.textMuted,
                  minWidth: 34,
                  textAlign: "center",
                }}
              >
                {activeIndex + 1}/{sessionItems.length}
              </span>
              <NavBtn
                disabled={activeIndex >= sessionItems.length - 1}
                onClick={() => goRel(1)}
                label={t("auditLogDetail.human.nextOp")}
                icon={<RightOutlined style={{ fontSize: 12 }} />}
              />
            </div>
          )}

          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 28,
              height: 28,
              borderRadius: 6,
              background: "transparent",
              border: "none",
              color: R.textMuted,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--color-shade-1)";
              e.currentTarget.style.color = R.ink;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = R.textMuted;
            }}
          >
            <CloseOutlined style={{ fontSize: 13 }} />
          </button>
        </div>

        {/* Body */}
        <div
          className="al-scroll"
          style={{ flex: 1, minHeight: 0, overflowY: "auto", background: R.bg }}
        >
          <div
            style={{
              padding: isNarrow ? "12px" : "16px",
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            {isLoading ? (
              <Skeleton active paragraph={{ rows: 6 }} />
            ) : error || !data ? (
              <Empty
                description={t("auditLogDetail.loadError")}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              <>
                {billId != null && sessionItems.length > 1 ? (
                  <BillTimeline
                    billId={billId}
                    items={sessionItems}
                    detailById={detailById}
                    activeId={activeEntry?.id ?? null}
                    activeDetail={data}
                    activeStory={story}
                    onSelect={navigateTo}
                    lang={i18n.language}
                    navTick={navTick}
                    isNarrow={isNarrow}
                  />
                ) : (
                  <DetailCard
                    detail={data}
                    story={story}
                    lang={i18n.language}
                    isNarrow={isNarrow}
                  />
                )}

                {/* Collapsible technical details */}
                <div>
                  <button
                    type="button"
                    onClick={() => setShowRaw((v) => !v)}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "10px 12px",
                      borderRadius: 10,
                      border: `1px solid ${R.border}`,
                      background: R.surface,
                      color: R.textSoft,
                      cursor: "pointer",
                      fontSize: 12.5,
                      fontWeight: 600,
                      transition: "background 120ms",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "var(--color-shade-1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = R.surface;
                    }}
                  >
                    <CodeOutlined
                      style={{ color: R.textMuted, fontSize: 14 }}
                    />
                    <span style={{ flex: 1, textAlign: "left" }}>
                      {t("auditLogDetail.human.rawTitle")}
                    </span>
                    {showRaw ? (
                      <DownOutlined
                        style={{ fontSize: 11, color: R.textMuted }}
                      />
                    ) : (
                      <RightOutlined
                        style={{ fontSize: 11, color: R.textMuted }}
                      />
                    )}
                  </button>

                  {showRaw && (
                    <div
                      style={{
                        marginTop: 10,
                        display: "flex",
                        flexDirection: "column",
                        gap: 10,
                      }}
                    >
                      <PayloadSection
                        title={`${t("auditLogDetail.request")} · ${t("auditLogDetail.body")}`}
                        data={data.request_body}
                        methodNote={
                          method === "GET"
                            ? t("auditLogDetail.getNoBody")
                            : undefined
                        }
                      />
                      <PayloadSection
                        title={`${t("auditLogDetail.response")} · ${t("auditLogDetail.body")}`}
                        data={data.response_body}
                      />
                      <PayloadSection
                        title={`${t("auditLogDetail.request")} · ${t("auditLogDetail.query")}`}
                        data={data.request_query}
                      />
                      <PayloadSection
                        title={`${t("auditLogDetail.request")} · ${t("auditLogDetail.headers")}`}
                        data={data.request_headers}
                      />
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}

function NavBtn({
  disabled,
  onClick,
  label,
  icon,
}: {
  disabled: boolean;
  onClick: () => void;
  label: string;
  icon: React.ReactNode;
}) {
  return (
    <Tooltip title={label}>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={label}
        style={{
          width: 26,
          height: 26,
          borderRadius: 6,
          border: "none",
          background: "transparent",
          color: disabled ? R.borderMed : R.textSoft,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: disabled ? "default" : "pointer",
          transition: "background 120ms, color 120ms",
        }}
        onMouseEnter={(e) => {
          if (!disabled) {
            e.currentTarget.style.background = "var(--color-shade-1)";
            e.currentTarget.style.color = R.ink;
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "transparent";
          e.currentTarget.style.color = disabled ? R.borderMed : R.textSoft;
        }}
      >
        {icon}
      </button>
    </Tooltip>
  );
}

function StatusPill({ ok }: { ok: boolean }) {
  const { t } = useTranslation();
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "3px 9px",
        borderRadius: 999,
        flexShrink: 0,
        background: ok ? R.successTint : R.dangerTint,
        color: ok ? R.successInk : R.dangerDeep,
        fontSize: 11.5,
        fontWeight: 700,
      }}
    >
      {ok ? (
        <CheckCircleFilled style={{ fontSize: 12 }} />
      ) : (
        <CloseCircleFilled style={{ fontSize: 12 }} />
      )}
      {ok
        ? t("auditLogDetail.human.successLabel")
        : t("auditLogDetail.human.failedLabel")}
    </span>
  );
}

// Един ред "етикет: стойност" — за единични факти (начин на плащане, ново име).
// Чист ред без фон/бордър, в стила на реда "Потребител: …".
function InlineFact({
  label,
  value,
  toneInk,
}: {
  label: string;
  value: string;
  toneInk: string;
}) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
      <span style={{ fontSize: 12.5, color: R.textSoft }}>{label}:</span>
      <span
        style={{
          fontSize: 13.5,
          fontWeight: 700,
          letterSpacing: -0.2,
          color: toneInk,
          fontVariantNumeric: "tabular-nums",
          wordBreak: "break-word",
        }}
      >
        {value}
      </span>
    </div>
  );
}

// ── Споделено тяло: факти + продукти + бележка + общо ────────────────────────

function StoryBody({
  story,
  toneInk,
  isNarrow,
  onTint,
}: {
  story: HumanStory;
  toneInk: string;
  isNarrow: boolean;
  // Когато тялото седи върху амбър фон (активен ред в хронологията),
  // вътрешните карти стават бели вместо сиво-в-жълто.
  onTint?: boolean;
}) {
  const { t } = useTranslation();
  const hasAny =
    story.facts.length > 0 ||
    story.lines.length > 0 ||
    story.note ||
    story.total;
  if (!hasAny) {
    return story.interpreted ? null : (
      <div
        style={{
          fontSize: 12.5,
          color: R.textMuted,
          fontStyle: "italic",
          lineHeight: 1.5,
        }}
      >
        {t("auditLogDetail.human.noDetails")}
      </div>
    );
  }
  const inlineFacts = story.facts.filter((f) => f.inline);
  const gridFacts = story.facts.filter((f) => !f.inline);
  return (
    <>
      {inlineFacts.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {inlineFacts.map((f, i) => (
            <InlineFact
              key={i}
              label={f.label}
              value={f.value}
              toneInk={f.tone ? TONES[f.tone].ink : R.ink}
            />
          ))}
        </div>
      )}

      {gridFacts.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isNarrow
              ? "1fr 1fr"
              : "repeat(auto-fit, minmax(150px, 1fr))",
            gap: 12,
            marginBottom:
              story.lines.length || story.total || story.note ? 14 : 0,
          }}
        >
          {gridFacts.map((f, i) => (
            <div key={i}>
              <div
                style={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: 0.8,
                  textTransform: "uppercase",
                  color: R.textMuted,
                  marginBottom: 3,
                }}
              >
                {f.label}
              </div>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  letterSpacing: -0.2,
                  color: f.tone ? TONES[f.tone].ink : R.ink,
                  fontVariantNumeric: "tabular-nums",
                  wordBreak: "break-word",
                }}
              >
                {f.value}
              </div>
            </div>
          ))}
        </div>
      )}

      {story.lines.length > 0 && (
        <div>
          {story.linesLabel && (
            <div
              style={{
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: 0.8,
                textTransform: "uppercase",
                color: R.textMuted,
                marginBottom: 8,
              }}
            >
              {story.linesLabel}
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            {story.lines.map((line, i) => (
              <LineRow
                key={i}
                line={line}
                isNarrow={isNarrow}
                onTint={onTint}
              />
            ))}
          </div>
        </div>
      )}

      {story.note && (
        <div
          style={{
            marginTop: story.lines.length || story.facts.length ? 14 : 0,
            padding: "10px 12px",
            borderRadius: 10,
            background: onTint ? R.surface : R.bg,
            border: `1px solid ${onTint ? R.borderMed : R.border}`,
            fontSize: 12.5,
            color: R.textSoft,
            lineHeight: 1.5,
          }}
        >
          {story.note}
        </div>
      )}

      {story.total && (
        <div
          style={{
            marginTop: 14,
            paddingTop: 12,
            borderTop: `1px solid ${R.border}`,
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 10,
          }}
        >
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.8,
              textTransform: "uppercase",
              color: R.textMuted,
            }}
          >
            {story.totalLabel ?? t("auditLogDetail.human.total")}
          </span>
          <span
            style={{
              fontSize: 22,
              fontWeight: 700,
              letterSpacing: -0.6,
              color: toneInk,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {story.total}
          </span>
        </div>
      )}
    </>
  );
}

function ReasonBanner({ reason }: { reason: string }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        padding: "8px 10px",
        borderRadius: 8,
        background: R.dangerTint,
      }}
    >
      <WarningOutlined
        style={{ color: R.dangerDeep, fontSize: 13, marginTop: 2 }}
      />
      <span style={{ fontSize: 12.5, color: R.dangerDeep, lineHeight: 1.45 }}>
        {reason}
      </span>
    </div>
  );
}

function MutedFooter({ detail }: { detail: AuditLogDetail }) {
  return (
    <div
      style={{
        fontSize: 10.5,
        color: R.textMuted,
        fontFamily: R.mono,
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
      }}
    >
      <span>
        {detail.method} {detail.path}
      </span>
      <span>· {detail.duration_ms} ms</span>
      <span>· HTTP {detail.status_code}</span>
    </div>
  );
}

// ── Standalone detail card (за записи без хронология: вход, каса, репорти) ────

function DetailCard({
  detail,
  story,
  lang,
  isNarrow,
}: {
  detail: AuditLogDetail;
  story: HumanStory | null;
  lang: string;
  isNarrow: boolean;
}) {
  const { t } = useTranslation();
  const tone: Tone = story?.tone ?? "neutral";
  const toneCfg = TONES[tone];
  const icon = KIND_ICONS[story?.kind ?? "generic"] ?? KIND_ICONS.generic;
  const status = statusInfo(detail);
  const when = useMemo(
    () => formatWhen(detail.timestamp, lang, t),
    [detail.timestamp, lang, t],
  );

  return (
    <section
      style={{
        background: R.surface,
        border: `1px solid ${R.border}`,
        borderRadius: 14,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: isNarrow ? "14px 14px" : "16px 18px",
          borderBottom: `1px solid ${R.border}`,
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            background: toneCfg.tint,
            color: toneCfg.ink,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 20,
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: 1.2,
              textTransform: "uppercase",
              color: R.textMuted,
              marginBottom: 2,
            }}
          >
            {t("auditLogDetail.human.summaryKicker")}
          </div>
          <div
            style={{
              fontSize: isNarrow ? 16 : 18,
              fontWeight: 700,
              letterSpacing: -0.3,
              color: R.ink,
              lineHeight: 1.2,
            }}
          >
            {detail.operation}
          </div>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isNarrow ? "1fr" : "1fr 1fr",
          gap: 1,
          background: R.border,
        }}
      >
        <Meta
          icon={<UserOutlined />}
          label={t("auditLogDetail.human.who")}
          value={detail.user_name || `#${detail.user_id ?? "—"}`}
        />
        <Meta
          icon={<ClockCircleOutlined />}
          label={t("auditLogDetail.human.when")}
          value={when}
          mono
        />
        {(detail.table_name || detail.bill_id != null) && (
          <Meta
            icon={<ProfileOutlined />}
            label={t("auditLogDetail.human.table")}
            value={
              [
                detail.table_name
                  ? `${t("auditLogDetail.human.table")} ${detail.table_name}`
                  : null,
                detail.bill_id != null ? `#${detail.bill_id}` : null,
              ]
                .filter(Boolean)
                .join(" · ") || "—"
            }
          />
        )}
      </div>

      <div
        style={{
          padding: isNarrow ? "12px 14px 14px" : "14px 18px 18px",
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        {!status.ok && status.reason && <ReasonBanner reason={status.reason} />}
        {story && (
          <StoryBody story={story} toneInk={toneCfg.ink} isNarrow={isNarrow} />
        )}
      </div>

      <div style={{ padding: "8px 18px", borderTop: `1px solid ${R.border}` }}>
        <MutedFooter detail={detail} />
      </div>
    </section>
  );
}

// ── Разгънат детайл на активния ред в хронологията ───────────────────────────

function ActiveRowDetail({
  detail,
  story,
  isNarrow,
}: {
  detail: AuditLogDetail;
  story: HumanStory | null;
  isNarrow: boolean;
}) {
  const { t } = useTranslation();
  const tone: Tone = story?.tone ?? "neutral";
  const toneCfg = TONES[tone];
  const status = statusInfo(detail);
  return (
    <div
      style={{
        marginTop: 10,
        paddingTop: 12,
        borderTop: `1px dashed ${R.borderMed}`,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <UserOutlined style={{ color: R.textMuted, fontSize: 13 }} />
        <span style={{ fontSize: 12.5, color: R.textSoft }}>
          {t("auditLogDetail.human.who")}:{" "}
          <span style={{ color: R.ink, fontWeight: 600 }}>
            {detail.user_name || `#${detail.user_id ?? "—"}`}
          </span>
        </span>
      </div>
      {!status.ok && status.reason && <ReasonBanner reason={status.reason} />}
      {story && (
        <StoryBody
          story={story}
          toneInk={toneCfg.ink}
          isNarrow={isNarrow}
          onTint
        />
      )}
      <MutedFooter detail={detail} />
    </div>
  );
}

// ── Bill journey timeline (активният ред е разгънат) ─────────────────────────

function BillTimeline({
  billId,
  items,
  detailById,
  activeId,
  activeDetail,
  activeStory,
  onSelect,
  lang,
  navTick,
  isNarrow,
}: {
  billId: number;
  items: AuditLogEntry[];
  detailById: Map<number, AuditLogDetail | undefined>;
  activeId: number | null;
  activeDetail: AuditLogDetail;
  activeStory: HumanStory | null;
  onSelect: (e: AuditLogEntry) => void;
  lang: string;
  navTick: number;
  isNarrow: boolean;
}) {
  const { t } = useTranslation();
  const activeRef = useRef<HTMLButtonElement>(null);
  const steps = buildTimeline(items, detailById, t);

  useEffect(() => {
    if (navTick === 0) return;
    activeRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [navTick]);

  return (
    <section
      style={{
        background: R.surface,
        border: `1px solid ${R.border}`,
        borderRadius: 14,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "12px 16px 10px",
          borderBottom: `1px solid ${R.border}`,
        }}
      >
        <div
          style={{
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: 1.2,
            textTransform: "uppercase",
            color: R.textMuted,
          }}
        >
          {t("auditLogDetail.human.timelineTitle")}
        </div>
        <div
          style={{
            fontSize: 13.5,
            fontWeight: 700,
            color: R.ink,
            marginTop: 2,
          }}
        >
          {t("auditLogDetail.human.billLabel")} #{billId}
          <span style={{ color: R.textMuted, fontWeight: 500 }}>
            {" · "}
            {items.length} {t("auditLogDetail.human.operationsWord")}
          </span>
        </div>
      </div>

      <div style={{ padding: "6px 8px 10px" }}>
        {steps.map((s, i) => {
          const active = s.id === activeId;
          const toneInk = s.ok ? TONES[s.tone].ink : R.dangerDeep;
          const time = new Date(s.timestamp).toLocaleTimeString(lang, {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          });
          const prevBal = i > 0 ? steps[i - 1].balance : null;
          const delta =
            s.balance != null && prevBal != null ? s.balance - prevBal : null;
          const showDelta = delta != null && Math.abs(delta) >= 0.005;
          return (
            <button
              key={s.id}
              ref={active ? activeRef : undefined}
              type="button"
              onClick={() => onSelect(items[i])}
              style={{
                width: "100%",
                textAlign: "left",
                display: "flex",
                alignItems: "stretch",
                gap: 8,
                padding: "8px 8px 8px 6px",
                borderRadius: 10,
                border: "none",
                background: active ? R.brandTint : "transparent",
                cursor: "pointer",
                position: "relative",
                transition: "background 120ms",
              }}
              onMouseEnter={(e) => {
                if (!active)
                  e.currentTarget.style.background = "var(--color-shade-1)";
              }}
              onMouseLeave={(e) => {
                if (!active) e.currentTarget.style.background = "transparent";
              }}
            >
              {active && (
                <span
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 6,
                    bottom: 6,
                    width: 3,
                    borderRadius: 3,
                    background: "var(--color-brand)",
                  }}
                />
              )}
              <span
                style={{
                  width: 18,
                  flexShrink: 0,
                  textAlign: "right",
                  paddingTop: 2,
                  fontFamily: R.mono,
                  fontSize: 11,
                  fontWeight: active ? 700 : 500,
                  color: active ? R.brandDeep : R.textMuted,
                }}
              >
                {i + 1}
              </span>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  alignSelf: "stretch",
                  flexShrink: 0,
                  paddingTop: 4,
                }}
              >
                <span
                  style={{
                    width: 11,
                    height: 11,
                    borderRadius: 999,
                    background: toneInk,
                    boxShadow: active ? `0 0 0 3px ${R.brandTint}` : "none",
                    flexShrink: 0,
                  }}
                />
                {i < steps.length - 1 && (
                  <span
                    style={{
                      flex: 1,
                      width: 2,
                      background: R.border,
                      marginTop: 2,
                    }}
                  />
                )}
              </div>

              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  paddingBottom: i < steps.length - 1 ? 6 : 0,
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "baseline", gap: 8 }}
                >
                  <span
                    style={{
                      fontFamily: R.mono,
                      fontSize: 11,
                      color: R.textMuted,
                      flexShrink: 0,
                    }}
                  >
                    {time}
                  </span>
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: 13,
                      fontWeight: active ? 700 : 600,
                      color: s.ok ? R.ink : R.dangerDeep,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {s.operation}
                  </span>
                  {s.balance != null && (
                    <span
                      style={{
                        fontSize: 12.5,
                        fontWeight: 700,
                        color: R.ink,
                        fontVariantNumeric: "tabular-nums",
                        flexShrink: 0,
                      }}
                    >
                      {eur(s.balance)}
                    </span>
                  )}
                </div>
                {(s.flag || showDelta) && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginTop: 3,
                    }}
                  >
                    {s.flag && (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 5,
                          fontSize: 11,
                          fontWeight: 600,
                          color: R.dangerDeep,
                        }}
                      >
                        <WarningOutlined style={{ fontSize: 11 }} />
                        {s.flag}
                      </span>
                    )}
                    {showDelta && (
                      <span
                        style={{
                          marginLeft: "auto",
                          fontSize: 11,
                          fontWeight: 700,
                          fontVariantNumeric: "tabular-nums",
                          color: delta! > 0 ? TONES.add.ink : TONES.remove.ink,
                          flexShrink: 0,
                        }}
                      >
                        {delta! > 0 ? "+" : "−"}
                        {eur(Math.abs(delta!))}
                      </span>
                    )}
                  </div>
                )}

                {/* Разгънат детайл на активната операция — всичко на едно място */}
                {active && (
                  <ActiveRowDetail
                    detail={activeDetail}
                    story={activeStory}
                    isNarrow={isNarrow}
                  />
                )}
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function Meta({
  icon,
  label,
  value,
  mono,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div
      style={{
        background: R.surface,
        padding: "10px 14px",
        display: "flex",
        alignItems: "center",
        gap: 10,
        minWidth: 0,
      }}
    >
      <span style={{ color: R.textMuted, fontSize: 14, flexShrink: 0 }}>
        {icon}
      </span>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 0.8,
            textTransform: "uppercase",
            color: R.textMuted,
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: R.ink,
            fontFamily: mono ? R.mono : undefined,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={value}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

// Ширини на дясно-подравнените числови колони — споделени от продукта и
// добавките, за да са количества/цени/суми точно едно под друго.
const COL = { qty: 30, unit: 66, total: 78, gap: 8 };

function NumCols({
  qty,
  unit,
  total,
  qtyColor,
  unitColor,
  totalColor,
  size,
  totalWeight,
}: {
  qty?: string;
  unit?: string;
  total?: string;
  qtyColor: string;
  unitColor: string;
  totalColor: string;
  size: number;
  totalWeight: number;
}) {
  const cell = (width: number, text: string, color: string, weight: number) => (
    <span
      style={{
        width,
        flexShrink: 0,
        textAlign: "right",
        fontSize: size,
        fontWeight: weight,
        color,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {text}
    </span>
  );
  return (
    <>
      {cell(COL.qty, qty ? `${qty}×` : "", qtyColor, 600)}
      {cell(COL.unit, unit ?? "", unitColor, 500)}
      {cell(COL.total, total ?? "", totalColor, totalWeight)}
    </>
  );
}

function LineRow({
  line,
  onTint,
}: {
  line: {
    qty?: string;
    name: string;
    unit?: string;
    total?: string;
    tone?: Tone;
    addons?: { qty: string; name: string; unit?: string; total: string }[];
    subs?: string[];
  };
  isNarrow: boolean;
  // Бяла карта върху амбър фон, бежова рецесирана върху бяла карта.
  onTint?: boolean;
}) {
  const toneInk = line.tone ? TONES[line.tone].ink : R.ink;
  return (
    <div
      style={{
        background: onTint ? R.surface : R.bg,
        border: `1px solid ${onTint ? R.borderMed : R.border}`,
        borderRadius: 10,
        padding: "5px 12px",
      }}
    >
      {/* Продуктов ред: име отляво · количество × ед. цена · сума отдясно */}
      <div style={{ display: "flex", alignItems: "baseline", gap: COL.gap }}>
        <div
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 13.5,
            fontWeight: 700,
            color: R.ink,
            letterSpacing: -0.2,
            wordBreak: "break-word",
          }}
        >
          {line.name}
        </div>
        <NumCols
          qty={line.qty}
          unit={line.unit}
          total={line.total}
          qtyColor={toneInk}
          unitColor={line.tone === "remove" ? toneInk : R.textSoft}
          totalColor={line.tone === "remove" ? toneInk : R.ink}
          size={13.5}
          totalWeight={700}
        />
      </div>

      {/* Добавки — в същите колони като продукта */}
      {line.addons?.map((a, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: COL.gap,
            marginTop: 5,
          }}
        >
          <div
            style={{
              flex: 1,
              minWidth: 0,
              paddingLeft: 12,
              fontSize: 12,
              color: R.textMuted,
              lineHeight: 1.4,
              wordBreak: "break-word",
            }}
          >
            + {a.name}
          </div>
          <NumCols
            qty={a.qty}
            unit={a.unit}
            total={a.total}
            qtyColor={R.textMuted}
            unitColor={R.textMuted}
            totalColor={R.textSoft}
            size={11.5}
            totalWeight={600}
          />
        </div>
      ))}

      {/* Бележки: отстъпка, модификатор */}
      {line.subs && line.subs.length > 0 && (
        <div
          style={{
            marginTop: 5,
            paddingLeft: 12,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {line.subs.map((s, i) => (
            <div
              key={i}
              style={{ fontSize: 11.5, color: R.textMuted, lineHeight: 1.4 }}
            >
              {s}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Payload section: title + copy + wrap toggle + scrollable JSON block ──

function PayloadSection({
  title,
  data,
  methodNote,
}: {
  title: string;
  data: unknown;
  methodNote?: string;
}) {
  const { t } = useTranslation();
  const [pretty, setPretty] = useState(true);
  const [copied, setCopied] = useState(false);

  const truncatedSize = useMemo(() => {
    if (
      data != null &&
      typeof data === "object" &&
      (data as Record<string, unknown>).__truncated__ === true
    ) {
      const orig = (data as Record<string, unknown>).__original_size__;
      return typeof orig === "number" ? orig : null;
    }
    return undefined;
  }, [data]);

  const isEmpty =
    data == null ||
    (typeof data === "object" && Object.keys(data as object).length === 0);

  const jsonString = useMemo(() => {
    if (data == null) return "";
    try {
      return JSON.stringify(data, null, pretty ? 2 : 0);
    } catch {
      return String(data);
    }
  }, [data, pretty]);

  const copyableText = useMemo(() => {
    if (data == null) return "";
    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  }, [data]);

  const handleCopy = async () => {
    if (!copyableText) return;
    try {
      await navigator.clipboard.writeText(copyableText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard write can fail in non-secure contexts — silently ignore
    }
  };

  return (
    <section
      style={{
        background: R.surface,
        border: `1px solid ${R.border}`,
        borderRadius: 10,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        minWidth: 0,
      }}
    >
      <header
        style={{
          padding: "6px 8px 6px 12px",
          borderBottom: `1px solid ${R.border}`,
          display: "flex",
          alignItems: "center",
          gap: 8,
          minHeight: 32,
          flexShrink: 0,
        }}
      >
        <span
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: 0.8,
            textTransform: "uppercase",
            color: R.textMuted,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={title}
        >
          {title}
        </span>
        {!isEmpty && truncatedSize === undefined && (
          <>
            <button
              type="button"
              onClick={() => setPretty((v) => !v)}
              style={toggleBtnStyle(pretty)}
              title={
                pretty
                  ? t("auditLogDetail.toggleRaw")
                  : t("auditLogDetail.togglePretty")
              }
            >
              {pretty ? "{ }" : "—"}
            </button>
            <Tooltip
              title={
                copied ? t("auditLogDetail.copied") : t("auditLogDetail.copy")
              }
            >
              <button
                type="button"
                onClick={handleCopy}
                style={iconBtnStyle(copied)}
                aria-label={t("auditLogDetail.copy")}
              >
                {copied ? (
                  <CheckOutlined style={{ fontSize: 12 }} />
                ) : (
                  <CopyOutlined style={{ fontSize: 12 }} />
                )}
              </button>
            </Tooltip>
          </>
        )}
      </header>

      {truncatedSize !== undefined ? (
        <div
          style={{
            flex: 1,
            minHeight: 0,
            padding: "12px 14px",
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
            background: R.dangerTint,
          }}
        >
          <WarningOutlined
            style={{ color: R.dangerDeep, fontSize: 14, marginTop: 2 }}
          />
          <div style={{ fontSize: 12, color: R.dangerDeep, lineHeight: 1.4 }}>
            <div style={{ fontWeight: 700, marginBottom: 2 }}>
              {t("auditLogDetail.truncatedTitle")}
            </div>
            <div>
              {truncatedSize !== null
                ? t("auditLogDetail.truncatedBody", {
                    size: (truncatedSize / 1024).toFixed(1),
                  })
                : t("auditLogDetail.truncatedBodyUnknown")}
            </div>
          </div>
        </div>
      ) : isEmpty ? (
        <div style={{ padding: 10, display: "flex" }}>
          <div
            style={{
              flex: 1,
              padding: "10px 12px",
              fontSize: 12,
              color: R.textMuted,
              fontStyle: "italic",
              background: R.bg,
              border: `1px solid ${R.borderMed}`,
              borderRadius: 8,
              boxShadow: "inset 0 1px 2px rgba(17,17,17,0.04)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            {methodNote ?? t("auditLogDetail.empty")}
          </div>
        </div>
      ) : (
        <div style={{ padding: 10, display: "flex" }}>
          <pre
            className="al-code-block"
            style={{
              flex: 1,
              minHeight: 0,
              maxHeight: 320,
              margin: 0,
              padding: "10px 12px",
              fontFamily: R.mono,
              fontSize: 12,
              lineHeight: 1.55,
              color: R.text,
              whiteSpace: pretty ? "pre" : "pre-wrap",
              wordBreak: pretty ? "normal" : "break-all",
              background: R.bg,
              border: `1px solid ${R.borderMed}`,
              borderRadius: 8,
              boxShadow: "inset 0 1px 2px rgba(17,17,17,0.04)",
              overflow: "auto",
            }}
          >
            {jsonString}
          </pre>
        </div>
      )}
    </section>
  );
}

function iconBtnStyle(active: boolean): React.CSSProperties {
  return {
    width: 26,
    height: 26,
    borderRadius: 6,
    border: "none",
    background: active ? R.brandTint : "transparent",
    color: active ? R.brandDeep : R.textMuted,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "background 120ms, color 120ms",
  };
}

function toggleBtnStyle(active: boolean): React.CSSProperties {
  return {
    height: 24,
    minWidth: 32,
    padding: "0 8px",
    borderRadius: 6,
    border: `1px solid ${active ? "var(--color-brand)" : R.border}`,
    background: active ? R.brandTint : "transparent",
    color: active ? R.brandDeep : R.textSoft,
    fontFamily: R.mono,
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
    transition: "background 120ms, color 120ms, border-color 120ms",
  };
}
